import React, { useEffect, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import PuzzlePiece from '@decisiv/iconix/lib/components/PuzzlePiece';
import Grid from '@decisiv/iconix/lib/components/Grid';

import {
  Avatar,
  Flex,
  LeftNav,
  Menu,
  TopNav,
  useStickyLeftNav,
} from '@decisiv/ui-components/lib/components';

import styled from 'styled-components';
import rem from 'polished/lib/helpers/rem';
import color from '@decisiv/design-tokens/lib/color';

import focusRingWithColor from '@decisiv/ui-components/lib/utils/focusRingWithColor';
import getEnvironment from '../utils/getCurrentEnv';
import NavFooter from '../components/NavFooter';
import Paper from '../components/Paper';
import IntegrationsMenu from '../components/IntegrationsMenu';
import { IntegrationsActionsProvider } from '../contexts/IntegrationsActionsContext';
import WhatsNewMenu from '../components/WhatsNew';
import NavMenu from '../components/NavMenu';
import NightModeToggle from '../components/NightModeToggle';

import AvatarProd from '../images/logo.png';
import AvatarStaging from '../images/AvatarStaging.png';
import AvatarQa from '../images/AvatarQA.png';
import { encryptStorage } from '../utils/storage';
import '../styles/HomePage.css';
import authorizeOn from '../utils/resourceAuthorizer';
import localCache from '../utils/localCache';
import { STORAGE_KEYS } from '../utils/storageKeys';

const RouteOutlet = styled.div`
  width: 100%;

  /* Focus lands here programmatically on every navigation. :focus-visible keeps
     the ring for the keyboard user who triggered it — they need to see focus
     leave the nav — and drops it after a pointer click, which would otherwise
     box the whole page. The ring itself is the KDS one. */
  &:focus {
    outline: none;
  }

  &:focus-visible {
    ${focusRingWithColor(color.opacity.charcoal40)}
  }
`;

const Container = styled(Flex).attrs({ as: 'section' })`
  background-color: var(--if-bg);
  height: calc(100vh - 60px);
  overflow: auto;
  padding: 0;
`;

export default function HomePage() {
  // Read from the router, not window.location: this layout route stays mounted
  // across child navigations, so a window read would highlight a stale nav item.
  const { pathname, search } = useLocation();
  const routeOutletRef = useRef(null);
  const previousPathname = useRef(pathname);

  // @reach/router moved focus to the routed content on every navigation, so a
  // screen reader announced the new page instead of leaving the user on the nav
  // control they just activated. Skipped on first render, as reach did.
  useEffect(() => {
    if (previousPathname.current === pathname) return;
    previousPathname.current = pathname;
    routeOutletRef.current?.focus();
  }, [pathname]);
  const userSession = encryptStorage.getItem('userSession');
  const userName = `${userSession?.first_name} ${userSession?.last_name}`;

  // TopNav.Menu defaults its dropdown min-width to the button width + 28px,
  // making the Profile/Logout box overhang the user-name button. Observe the
  // button and pin the dropdown to its exact width instead.
  const userMenuRef = useRef(null);
  const [userMenuWidth, setUserMenuWidth] = useState(null);
  useEffect(() => {
    const node = userMenuRef.current;
    if (!node) return undefined;

    const measure = () => setUserMenuWidth(node.offsetWidth || null);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [userName]);

  /* eslint-disable no-unused-vars */
  const [{ isExpanded, navExpandedState }, handleChangeNavExpandedState] =
    useStickyLeftNav({
      getPreferredNavExpandedState: () =>
        localCache.get(STORAGE_KEYS.LEFT_NAV_STATE),
      setPreferredNavExpandedState: (state) =>
        state
          ? localCache.set(STORAGE_KEYS.LEFT_NAV_STATE, state)
          : localCache.remove(STORAGE_KEYS.LEFT_NAV_STATE),
    });

  const items = [
    {
      icon: Grid,
      text: 'Integrations',
      path: '/integrations',
      items: [
        {
          text: 'Archive',
          path: '/integrations/archive',
        },
        ...(authorizeOn()
          ? [
              {
                text: 'Integration Health',
                path: '/toolkit/integration_health',
              },
            ]
          : []),
      ],
    },
    {
      icon: PuzzlePiece,
      text: 'Toolkit',
      items: [
        {
          text: 'Log Inspector',
          path: '/toolkit/log_inspector',
        },
        {
          text: 'Commands',
          path: '/toolkit/commands',
        },
        {
          text: 'Actions',
          path: '/toolkit/actions',
        },
        {
          text: 'Errors',
          path: '/toolkit/errors',
        },
        {
          text: 'WMI Codes',
          path: '/toolkit/wmi_codes',
        },
        {
          text: 'Shared Settings',
          path: '/toolkit/shared_settings',
        },
        ...(authorizeOn()
          ? [
              {
                text: 'Decisiv Integration Lambda',
                path: '/toolkit/integration_lambdas',
              },
            ]
          : []),
        {
          text: "What's New",
          path: '/whats-new',
        },
      ],
    },
  ];

  const userMenuItems = [
    { id: 'user-profile', text: 'Profile', linkTo: '/user' },
    { id: 'logout', text: 'Logout', linkTo: '/logout' },
  ];

  const currentEnv = getEnvironment(process.env.REACT_APP_BASE_URL);

  const getImageByEnv = () => {
    const environment = currentEnv || 'qa';

    const images = {
      qa: AvatarQa,
      staging: AvatarStaging,
      prod: AvatarProd,
    };

    return images[environment] || images.qa;
  };

  return (
    <Paper css="height: 100%" padding={0}>
      {userSession ? (
        <Flex alignItems="top" flexDirection="row" padding={0}>
          <Flex flexDirection="column" className="if-leftnav">
            <LeftNav
              currentPath={pathname}
              items={items}
              navExpandedState={navExpandedState}
              onChangeNavExpandedState={handleChangeNavExpandedState}
              name="leftNaveToogle"
            />
            <NavFooter navExpandedState={isExpanded} />
          </Flex>

          <Paper padding={0}>
            <IntegrationsActionsProvider>
              <TopNav className="StretchedTopNav">
                <Flex justifyContent="space-between" style={{ width: '100%' }}>
                  <Flex paddingX={1}>
                    <TopNav.BrandInfo
                      logo={getImageByEnv()}
                      primaryText="Decisiv"
                      secondaryText="Integration Framework"
                    />
                  </Flex>
                  <Flex style={{ marginLeft: 'auto' }}>
                    {authorizeOn() && (
                      <Flex>
                        <IntegrationsMenu />
                      </Flex>
                    )}
                    <NightModeToggle />
                    <Flex marginLeft={1}>
                      <WhatsNewMenu />
                    </Flex>
                    {/* The right inset keeps the dropdown clear of the popper's
                        5px viewport-edge guard so it aligns flush with the
                        button instead of skidding left. */}
                    <Flex marginRight={0.5}>
                      <NavMenu
                        triggerRef={userMenuRef}
                        contentRole="menu"
                        childMinWidth={
                          userMenuWidth ? rem(userMenuWidth) : undefined
                        }
                        emblem={(emblemProps) => (
                          <Avatar
                            {...emblemProps}
                            name={userName}
                            indicatorColor="information"
                          />
                        )}
                        primaryText={userName}
                      >
                        <Menu items={userMenuItems} />
                      </NavMenu>
                    </Flex>
                  </Flex>
                </Flex>
              </TopNav>
              <Container
                alignItems="left"
                flexDirection="column"
                paddingY={0.5}
              >
                {/* @reach/router wrapped the matched route in its own focus
                    div. It kept page content out of this flex container —
                    several page styles set `order`, which only applies to flex
                    items — and it moved focus here on navigation so the new
                    page is announced. Both behaviours are reproduced. */}
                <RouteOutlet ref={routeOutletRef} tabIndex={-1}>
                  <Outlet />
                </RouteOutlet>
              </Container>
            </IntegrationsActionsProvider>
          </Paper>
        </Flex>
      ) : (
        // Carries the query the way @reach/router's Redirect did: the SSO round
        // trip comes back with `?sso_token=`, and dropping it here lands the
        // user on a bare sign-in form.
        <Navigate replace to={`/sign_in${search}`} />
      )}
    </Paper>
  );
}
